// language.service.ts
import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {StorageService} from './storage.service';

export type AppLanguage = 'de' | 'en';

@Injectable({providedIn: 'root'})
export class LanguageService {
    private readonly supported: AppLanguage[] = ['de', 'en'];
    private readonly fallback: AppLanguage = 'de';

    constructor(
        private translate: TranslateService,
        private storage: StorageService,
        @Inject(DOCUMENT) private document: Document
    ) {
    }

    init(): Observable<any> {
        const saved = this.storage.getLanguage();
        const browser = (this.translate.getBrowserLang() || '').toLowerCase() as AppLanguage;

        const initial =
            (saved && this.isSupported(saved) && saved) ||
            (browser && this.isSupported(browser) && browser) ||
            this.fallback;

        return this.use(initial);
    }

    use(lang: AppLanguage): Observable<any> {
        const next = this.isSupported(lang) ? lang : this.fallback;
        this.storage.setLanguage(next);
        this.document.documentElement.lang = next;
        return this.translate.use(next);
    }

    current(): AppLanguage {
        const cur = (this.translate.currentLang || this.translate.defaultLang || this.fallback) as AppLanguage;
        return this.isSupported(cur) ? cur : this.fallback;
    }

    route(path: string): string {
        return this.current() === 'en' ? `/en${path}` : path;
    }

    private isSupported(lang: string): lang is AppLanguage {
        return this.supported.includes(lang as AppLanguage);
    }
}
